import { FC } from "react";
import {
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@capacit-com/lara-law-shared-ui";
import { Building2, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface DashboardAudienceChoiceProps {
  idPrefix: string;
  isOrganizationDashboard: boolean;
  onChange: (isOrganizationDashboard: boolean) => void;
  /** The current tenant's name, to say who "Organisation" reaches. */
  tenantName?: string;
  disabled?: boolean;
}

const PERSONAL = "personal";
const ORGANISATION = "organisation";

/** Who a new dashboard is for, chosen before it is named (LAR-661). */
const DashboardAudienceChoice: FC<DashboardAudienceChoiceProps> = ({
  idPrefix,
  isOrganizationDashboard,
  onChange,
  tenantName,
  disabled,
}) => {
  const options = [
    {
      value: PERSONAL,
      icon: User,
      title: "Personligt",
      description: "Kun du kan se og redigere det.",
    },
    {
      value: ORGANISATION,
      icon: Building2,
      title: "Organisation",
      description: `Alle i ${tenantName ?? "organisationen"}. Starter som kladde, indtil du publicerer.`,
    },
  ];

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="mb-2 text-sm font-medium">Type</legend>
      <RadioGroup
        id={`${idPrefix}-audience`}
        value={isOrganizationDashboard ? ORGANISATION : PERSONAL}
        onValueChange={(value) => onChange(value === ORGANISATION)}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {options.map(({ value, icon: Icon, title, description }) => {
          const id = `${idPrefix}-audience-${value}`;
          const checked = (value === ORGANISATION) === isOrganizationDashboard;
          return (
            <Label
              key={value}
              htmlFor={id}
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 font-normal",
                checked ? "border-primary bg-primary/10" : "border-border"
              )}
            >
              <RadioGroupItem value={value} id={id} className="mt-0.5" />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <Icon className="size-3.5" aria-hidden="true" />
                  {title}
                </span>
                <span className="text-xs text-muted-foreground">
                  {description}
                </span>
              </span>
            </Label>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
};

export default DashboardAudienceChoice;
